import SkyOSMap from '@smartlinkdev/maps';
import type { Models, SkyOS } from '@smartlinkdev/types';
import {
  currentFloor,
  defaultFloor,
  mapFloors,
  hideControls,
  windowWidth,
  leftMenu,
} from '$lib/stores';
import {
  MapEvents,
  type MapServiceConstructorConfig,
  type AnimateConfig,
  type LocationClick,
  type PaddingOptions,
  type LngLat, type AnyToAnyConfig,
} from '$lib/types';
import { get } from 'radash';
import EventEmitter from 'events';
import { get as storeGet } from 'svelte/store';

export class MapService extends EventEmitter {
  private debug = false;
  private mapInstance: SkyOSMap;
  private settings: SkyOS.MapSettings;

  private static instance: MapService;
  private mapPins: SkyOS.Maps.Marker[] = [];
  private highlightColor = '#cc0000';
  private hoverModal: any;

  public constructor(config: MapServiceConstructorConfig) {
    super();
    this.debug = config.debug;
    this.settings = config.settings;
    config.settings.logoPosition = this.getLogoPosition();
    config.settings.disableAutoSwitchFloors = true;

    config.browserSettings.mapLoadData = {
      type: 'embed',
      venue_id: this.settings.venue.id,
    }

    this.mapInstance = new SkyOSMap(
      config.settings,
      this.debug,
      config.browserSettings,
      {
        logLevel: this.debug ? 'silly' : 'error',
      }
    );
    this.highlightColor = get<SkyOS.MapSettings, string>(this.settings, 'venue.settings.mapboxSettings.buildingHighlightColor', "#333333") || '#333333';

    this.mapInstance.addLogTransport(console);

    this.mapInstance.on(MapEvents.JS_READY, () => {
      this.jsReadyHandler();
    });

    this.mapInstance.on(MapEvents.LOCATION_CLICK, (e: LocationClick) => {
      this.locationClickHandler(e.location);
    });

    this.mapInstance.on(MapEvents.READY, () => {
      this.readyHandler();
    });

    this.mapInstance.on(MapEvents.FLOOR_CHANGE, (newFloor: Models.Floor) => {
      this.emit(MapEvents.FLOOR_CHANGE, newFloor);
      currentFloor.set(newFloor);
    });

    this.mapInstance.on(MapEvents.ERROR, (err: Error) => {
      this.errorHandler(err);
    });

    this.mapInstance.appendRemoteScriptToHead();
  }

  public static getInstance(config?: MapServiceConstructorConfig): MapService {
    if (!MapService.instance && !config) {
      throw new Error('unable to get nonexistent instance');
    }
    if (!MapService.instance && config) {
      MapService.instance = new MapService(config);
    }
    return MapService.instance;
  }

  public getLocationCenter(locationId: number): SkyOS.FoundFeature {
    return this.mapInstance.findBuildingByLocationId(locationId, true);
  }

  public animate(config: AnimateConfig): void {
    this.mapInstance.animate(config);
  }

  public changeFloor(newFloor: SkyOS.Maps.Floor): void {
    this.mapInstance.changeFloor({
      newFloor,
      displayNotification: false,
    });
  }

  public flyinToLocationById(locationId: number): void {
    const padding = 0.0005;
    const _windowWidth = storeGet(windowWidth);

    let padRight =  _windowWidth * .26;
    let padLeft = 0;

    const _leftMenu = storeGet(leftMenu);
    if (_leftMenu) {
      padRight = 0;
      padLeft = _windowWidth * .26;
    }

    this.mapInstance.flyInToLocation(locationId, padding, {
      top: 0,
      left: padLeft,
      bottom: 0,
      right: padRight,
    });
  }

  public highlightLocationById(locationId: number, color: string): void {
    this.mapInstance.highlightLocation({
      location: {
        id: locationId,
      },
      color,
    });
  }

  public removeRoutes(): void {
    this.mapInstance.removeRoutes();
  }

  public removeHighlights(): void {
    this.mapInstance.removeHighlights();
  }

  public reset(): void {
    this.mapInstance.reset({
      notify: (): void => {
        return;
      },
    });
  }

  public routeToLocationById(locationId: number): void {
    this.mapInstance.routeToLocation(
      {
        location: {
          id: locationId,
        },
      },
      {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      }
    );
  }

  public getRouteStringPieces(locString: string): {id: number, type: string} {
    const pieces = locString.split('|');
    const id = parseInt(pieces[1]);
    const type = pieces[0];
    return {id, type};
  }

  public routeAnyToAny(config: AnyToAnyConfig): void {
    let padRight =  config.paddingRight;
    let padLeft = 0;

    const _leftMenu = storeGet(leftMenu);
    if (_leftMenu) {
      padRight = 0;
      padLeft = config.paddingRight || 0;
    }

    const start = this.getRouteStringPieces(config.startIdString);
    const end = this.getRouteStringPieces(config.endIdString);

    try {
      const opts = {
        start: {
          id: start.id,
          type: start.type,
        },
        end: {
          id: end.id,
          type: end.type,
        },
        handicap: config.handicapMode
      };
      const paddingOpts = {
          top: 0,
          right: padRight,
          bottom: 0,
          left: padLeft,
      };
      if (this.mapInstance)
      this.mapInstance.routeAnyToAny(opts, paddingOpts);
    } catch (e) {
      console.error("Unable to create any to any route: ", e);
    }

  }

  public routeLocationToLocation(startLocId: number, endLocId: number, handicapMode = false, paddingRight = 0): void {

    let padRight =  paddingRight;
    let padLeft = 0;

    const _leftMenu = storeGet(leftMenu);
    if (_leftMenu) {
      padRight = 0;
      padLeft = paddingRight;
    }

    this.mapInstance.routeLocationToLocation({
      startLocation: {
        id: startLocId,
      },
      endLocation: {
        id: endLocId,
      },
      handicap: handicapMode
    },
    {
      top: 0,
      right: padRight,
      bottom: 0,
      left: padLeft,
    });
  }

  public clearMarkers(): void {
    this.mapPins.forEach((pin) => {
      this.mapInstance.removeMarker(pin);
    });
    this.mapPins = [];
  }

  public removeMarker(marker: SkyOS.Maps.Marker): void {
    this.mapInstance.removeMarker(marker);
  }

  public setMarker(opts: { html: string; coord: LngLat, anchor?: string }, clear = false): SkyOSMap.Maps.Marker {
    if (clear) {
      this.mapPins.forEach((pin) => {
        this.mapInstance.removeMarker(pin);
      });
      this.mapPins = [];
    }
    const pin = this.mapInstance.setMarker(opts);
    this.mapPins.push(pin);
    return pin;
  }

  public setPadding(opts: PaddingOptions): void {
    this.mapInstance.setPadding(opts);
  }

  public showAmenityType(amenityTypeId: number): void {
    this.mapInstance.clearPins();
    this.mapInstance.showAmenityType(amenityTypeId);
  }

  public resetAmenities(): void {
    this.mapInstance.clearPins();
    this.mapInstance.displayDefaultAmenities();
  }

  public showFullMap(zoom: number): void {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const newZoom = window.mapInMemory.getZoom() - zoom;
    this.mapInstance.zoom({ duration: 0.5, zoom: newZoom });
  }

  public zoom(changeAmount: number): void {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const newZoom = window.mapInMemory.getZoom() + changeAmount;
    this.mapInstance.zoom({ duration: 0.5, zoom: newZoom });
  }

  private errorHandler(err: Error): void {
    console.error('map error:', err); // TODO add mature logging
    this.emit(MapEvents.ERROR, err);
  }

  private getLogoPosition(): string {
    const _leftMenu = storeGet(leftMenu);
    let position = 'top-left';
    if (_leftMenu) {
      position = 'top-right';
    }
    return position;
  }

  private jsReadyHandler(): void {
    this.mapInstance.init();
    const hidecontrols = storeGet(hideControls);
    if (!hidecontrols) {
      this.mapInstance.addNavigationControls({
        position: this.getLogoPosition(),
        options: {
          visualizePitch: true,
        },
        showReset: false,
      });
    }
    this.emit(MapEvents.JS_READY);
  }

  private locationClickHandler(location: Models.Location): void {
    if (location && location.id) {
      this.emit(MapEvents.LOCATION_CLICK, location);
    }
  }

  private async readyHandler(): Promise<void> {
    const floors = get<SkyOS.MapSettings, Models.Floor[]>(
      this.settings,
      'venue.settings.mapboxSettings.floors'
    );
    if (floors) {
      mapFloors.set(floors);
      const defaultFl = floors.find((f) => f.default);
      if (defaultFl) {
        currentFloor.set(defaultFl);
        defaultFloor.set(defaultFl);
      }
    }

    // TODO set up webglcontextlost handler

    floors?.forEach((floor) => {
      (window as any).mapInMemory.on('mousemove', `floor-${floor.index}`, (e: any) => {
        if (e.features.length > 0) {
          const found = e.features.find((feature: any) => {
            return feature?.properties?.skyOSLocationId > 0;
          });
          if (found) {
            this.mapInstance.highlightLocation({
              location: {
                id: found.properties.skyOSLocationId,
              },
              color: this.highlightColor,
            });
            if (this.hoverModal) {
              this.mapInstance.removeMarker(this.hoverModal);
              this.hoverModal = undefined;
            }
            if (found.properties.name.trim() === '') {
              const foundLocation = this.settings.locations.find((location) => (location.id === found.properties.skyOSLocationId));
              if (foundLocation) {
                this.hoverModal = this.mapInstance.setMarker({
                  coord: {
                    lat: found.properties.door_y,
                    lng: found.properties.door_x,
                  },
                  html: `<div class="hoverModal">${foundLocation.name}</div>`,
                  anchor: 'top',
                });
              }
            }
          }
        }
      });
      (window as any).mapInMemory.on('mouseleave', `floor-${floor.index}`, () => {
        this.mapInstance.removeHighlights();
        if (this.hoverModal) {
          this.mapInstance.removeMarker(this.hoverModal);
          this.hoverModal = undefined;
        }
      });
    });

    setTimeout((): void => {
      this.emit(MapEvents.READY);
    }, 750);
  }

  public getLocationFloor(locationId: number): number[] {
    return this.mapInstance.getLocationFloor(locationId);
  }
 }
